import { Tooltip } from 'components/Basic/Tooltip/Tooltip';
import { InputHTMLAttributes } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { CSSProperties } from 'styled-components';
import { twJoin } from 'tailwind-merge';
import tinycolor from 'tinycolor2';
import { ExtractNativePropsFromDefault } from 'typeHelpers/ExtractNativePropsFromDefault';

type NativeProps = ExtractNativePropsFromDefault<
    InputHTMLAttributes<HTMLInputElement>,
    'name',
    'disabled' | 'id' | 'required'
>;

type CheckboxColorProps = NativeProps & {
    colorValue?: string;
    colorName: string;
    colorImage: string | null;
} & (
        | {
              value: boolean;
              fieldRef?: never;
              onChange: (...event: any[]) => void;
          }
        | {
              value?: never;
              fieldRef: ControllerRenderProps<any, any>;
              onChange?: never;
          }
    );

const CheckboxColor: FC<CheckboxColorProps> = ({
    colorImage,
    colorName,
    colorValue = '#d4d4d4',
    disabled,
    fieldRef,
    id,
    name,
    required,
    onChange,
    value,
}) => {
    const styles: CSSProperties = {
        ...(colorImage !== null ? { backgroundImage: `url(${colorImage})` } : { backgroundColor: colorValue }),
    };
    const inputId = id === undefined ? name + 'checkbox_color-id' : id;
    const isActive = fieldRef ? (fieldRef.value as boolean) : value;

    return (
        <div className={twJoin('relative flex h-6 w-6')}>
            {fieldRef ? (
                <input
                    checked={fieldRef.value}
                    disabled={disabled}
                    id={inputId}
                    name={fieldRef.name}
                    onBlur={fieldRef.onBlur}
                    onChange={fieldRef.onChange}
                    ref={fieldRef.ref}
                    required={required}
                    type="checkbox"
                    className="peer sr-only"
                />
            ) : (
                <input
                    checked={value}
                    disabled={disabled}
                    id={inputId}
                    name={name}
                    onChange={onChange}
                    required={required}
                    type="checkbox"
                    className="peer sr-only"
                />
            )}
            <Tooltip label={colorName}>
                <label
                    style={styles}
                    htmlFor={inputId}
                    className={twJoin(
                        'relative block w-6 cursor-pointer rounded-round text-[0px]',
                        isActive
                            ? 'after:opacity-1 after:pointer-events-none after:absolute after:left-2 after:top-1 after:h-[10px] after:w-[6px] after:rotate-45 after:border-b-2 after:border-r-2 after:border-solid after:border-transparent after:content-[""]'
                            : 'after:opacity-0',
                        tinycolor(colorValue).isLight() ? 'after:border-black' : 'after:border-white',
                        disabled &&
                            !isActive &&
                            'pointer-events-none opacity-30 after:top-1/2 after:left-1/2 after:flex after:-translate-x-1/2 after:-translate-y-1/2 after:items-center after:justify-center after:text-body after:opacity-100 after:content-["✕"]',
                        colorImage !== null
                            ? 'border-none bg-transparent bg-6 bg-center bg-no-repeat'
                            : 'border border-solid border-[#0c0c0c14]',
                    )}
                />
            </Tooltip>
        </div>
    );
};

/* @component */
export default CheckboxColor;
